import { toast } from '@affine/component';
import { databaseMoveMutation } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import {
  completeDatabaseCutMove,
  databaseCutMoveSignal,
} from '@blocksuite/affine/shared/adapters';
import { OnEvent, Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceDialogService } from '../../dialogs/services/workspace-dialog';
import { classifyOfflineMutationError } from '../../offline-mutation-outbox/services/offline-mutation-error';
import {
  OfflineMutationDomainError,
  type OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type {
  DatabaseMoveMutationPayload,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { Workspace, WorkspaceService } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import {
  appUsagesOfDatabase,
  fetchDocAppResourceLifecycleImpacts,
  formatAppResourceLifecycleImpact,
} from '../native-app/app-resource-lifecycle';

export const DATABASE_MOVE_CONFIRMATION = 'OFFLINE_DATABASE_MOVE_CONFIRMATION';
const DOMAIN_CODES = [
  'FORBIDDEN',
  'BAD_REQUEST',
  'NOT_FOUND',
  'OPERATION_TARGET_MISMATCH',
] as const;
type MoveRecord = OfflineMutationRecord<'database.move'>;

/** Durable moves and questions share the workspace lifetime, not an editor. */
@OnEvent(
  WorkspaceInitialized,
  (service: DatabaseMoveOutboxService) => service.onWorkspaceInitialized
)
export class DatabaseMoveOutboxService extends Service {
  private processing = false;
  private disposed = false;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly serverService: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly dialogs: WorkspaceDialogService
  ) {
    super();
    this.disposables.push(
      outbox.registerExecutor('database.move', record => this.send(record))
    );
    const failed = outbox.failedMutations$.subscribe(() =>
      this.scheduleQuestions()
    );
    const changed = dialogs.dialogs$.subscribe(() => this.scheduleQuestions());
    const refusal = outbox.failed$.subscribe(record => {
      if (
        record.operation !== 'database.move' ||
        record.lastError === DATABASE_MOVE_CONFIRMATION
      )
        return;
      if (record.failure === 'refused') {
        completeDatabaseCutMove(
          (record.payload as DatabaseMoveMutationPayload).operationId
        );
        toast(I18n['com.streamlain.databaseMove.refused']());
      }
    });
    this.disposables.push(() => {
      this.disposed = true;
      failed.unsubscribe();
      changed.unsubscribe();
      refusal.unsubscribe();
    });
  }

  onWorkspaceInitialized(_workspace: Workspace): void {}

  async request(
    input: DatabaseMoveMutationPayload,
    signal: AbortSignal
  ): Promise<void> {
    if (signal.aborted) return;
    const record = await this.outbox.enqueue({
      id: input.operationId,
      operation: 'database.move',
      payload: input,
      idempotencyKey: 'database.move:' + input.operationId,
      dedupePending: true,
      orderKey: 'database:' + input.databaseId,
    });
    const cancel = () => {
      void this.cancel(record).catch(console.error);
    };
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) cancel();
    const completed = this.outbox.completed$.subscribe(done => {
      if (done.id !== record.id) return;
      signal.removeEventListener('abort', cancel);
      completed.unsubscribe();
    });
    this.disposables.push(() => {
      signal.removeEventListener('abort', cancel);
      completed.unsubscribe();
    });
  }

  async confirm(
    input: DatabaseMoveMutationPayload,
    signal?: AbortSignal
  ): Promise<boolean> {
    const impacts = await this.impacts(input);
    if (signal?.aborted) return false;
    if (impacts.length === 0) return true;
    return new Promise(resolve => {
      let settled = false;
      const finish = (result?: boolean) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', abort);
        resolve(result === true);
      };
      const id = this.dialogs.open(
        'database-move-confirmation',
        {
          message: [
            I18n['com.streamlain.appDataMove.description'](),
            ...impacts.map(impact =>
              formatAppResourceLifecycleImpact(impact, I18n)
            ),
          ].join('\n'),
        },
        finish
      );
      const abort = () => this.dialogs.close(id, false);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
    });
  }

  private impacts(input: DatabaseMoveMutationPayload) {
    if (input.source.docId === input.target.docId) return Promise.resolve([]);
    return fetchDocAppResourceLifecycleImpacts(
      this.workspaceService.workspace.id,
      'move',
      [input.source.docId]
    ).then(impacts => appUsagesOfDatabase(impacts, input.databaseId));
  }

  private async cancel(record: MoveRecord): Promise<void> {
    // A confirmation record has been claimed, but no move request was sent.
    const question = this.outbox.failedMutations$.value.find(
      item =>
        item.id === record.id && item.lastError === DATABASE_MOVE_CONFIRMATION
    );
    if (question) await this.outbox.removeFailed(record.id);
    else await this.outbox.cancelPending(record.id);
  }

  private async send(record: MoveRecord): Promise<void> {
    const gql = this.serverService.server?.gql;
    if (!gql)
      throw new OfflineMutationTransientError('No server for database move');
    const { impactsConfirmed, ...payload } = record.payload;
    try {
      if (!impactsConfirmed && (await this.impacts(record.payload)).length > 0)
        throw new OfflineMutationDomainError(DATABASE_MOVE_CONFIRMATION);
      const engine = this.workspaceService.workspace.engine.doc;
      await engine.flushAndWaitForSynced(this.workspaceService.workspace.id);
      for (const docId of new Set([payload.source.docId, payload.target.docId]))
        await engine.flushAndWaitForSynced(docId);
      const result = await gql({
        query: databaseMoveMutation,
        variables: { input: { workspaceId: record.workspaceId, ...payload } },
      });
      completeDatabaseCutMove(
        payload.operationId,
        result.databaseMove.status === 'kept'
          ? undefined
          : {
              workspaceId: record.workspaceId,
              databaseId: payload.databaseId,
              docId: result.databaseMove.docId,
              blockId: result.databaseMove.blockId,
            }
      );
      if (result.databaseMove.status === 'kept')
        toast(I18n['com.streamlain.database.move.managerRequired']());
    } catch (error) {
      if (error instanceof OfflineMutationDomainError) throw error;
      throw classifyOfflineMutationError(error, DOMAIN_CODES);
    }
  }

  private scheduleQuestions(): void {
    if (this.processing || this.disposed) return;
    queueMicrotask(() => {
      void this.askPending().catch(console.error);
    });
  }

  private async askPending(): Promise<void> {
    if (
      this.processing ||
      this.disposed ||
      this.dialogs.dialogs$.value.some(
        d => d.type === 'database-move-confirmation'
      )
    )
      return;
    this.processing = true;
    try {
      const attempted = new Set<string>();
      while (!this.disposed) {
        const record = this.outbox.failedMutations$.value.find(
          item =>
            item.operation === 'database.move' &&
            item.lastError === DATABASE_MOVE_CONFIRMATION &&
            !attempted.has(item.id)
        ) as MoveRecord | undefined;
        if (!record) return;
        attempted.add(record.id);
        let confirmed: boolean;
        try {
          confirmed = await this.confirm(
            record.payload,
            databaseCutMoveSignal(record.payload.operationId)
          );
        } catch {
          continue;
        }
        if (this.disposed) return;
        // Escape/new cut may have removed this question while the dialog was open.
        if (!(await this.outbox.removeFailed(record.id))) continue;
        if (confirmed) {
          await this.outbox.enqueue({
            id: record.id,
            operation: 'database.move',
            payload: { ...record.payload, impactsConfirmed: true },
            idempotencyKey: record.idempotencyKey,
            dedupePending: true,
            orderKey: record.orderKey,
          });
        } else completeDatabaseCutMove(record.payload.operationId);
      }
    } finally {
      this.processing = false;
    }
  }
}
